import { InMemoryCache, from } from "@apollo/client";

import { ApolloClient } from "@apollo/client/core";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { ENVIRONMENT } from "./environment";
import { HttpLink } from "@apollo/client/link/http";
import VueApollo from "vue-apollo";
import { datadogRum } from "@datadog/browser-rum";
import { onError } from "@apollo/client/link/error";
import { routedClient } from "react-migration/lib/persistence/apollo/routedClient";

export const initialiseApolloProvider = ({
  accountsServicePrivateApiUrl,
  appEnv,
  constraintsApiUrl,
  graphqlGatewayUrl,
  loginUrl,
  planningServiceUrl,
}) => {
  const constraintsOptions =
    appEnv === "local"
      ? {
          headers: {
            user_id: "something",
            features: "readConstraints",
          },
        }
      : {};
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    const messages = {
      ClientParseError: "Request could not be sent",
      ServerParseError: "Server unavailable - please try later",
      ServerError: "Server unavailable - please try later",
      Object: "Unable to sync data, please try again",
    };

    if (appEnv !== "production" && graphQLErrors) {
      graphQLErrors.forEach((err) => console.log(err));
    }

    if (networkError) {
      if (networkError?.statusCode === 413 && graphQLErrors?.[0]?.extensions?.code === "CONTENT_TOO_LARGE") {
        // If the saved site is too large to return results we'll instead show a
        // message on the UI to the user.
        return;
      }

      if (networkError?.statusCode === 401) {
        datadogRum.addAction("graphql-error-logout", {
          errorLink: "apollo",
        });
        const url = new URL(loginUrl);
        url.searchParams.set(
          "redirect",
          window.location.pathname.replace(ENVIRONMENT.BASE_URL || "", "") + window.location.search
        );
        window.location.assign(url);
      } else if (networkError?.statusCode === 410) {
        if (window.confirm("Your application is out of date. Refresh the page now?")) {
          window.location.reload();
        }
      } else if (messages[networkError?.name]) {
        window.alert(messages[networkError.name] || messages.Object);
      }
    }
  });
  const networkInterfaces = {
    accountServicePrivateApi: new BatchHttpLink({
      uri: accountsServicePrivateApiUrl + "/graphql",
    }),
    gateway: new HttpLink({
      uri: graphqlGatewayUrl,
    }),
    planningService: new HttpLink({
      uri: planningServiceUrl + "/graphql",
    }),
  };

  const sharedClientConfig = {
    connectToDevTools: false,
    addTypename: true,
  };

  const apolloConstraintsClient = new ApolloClient({
    link: from([
      errorLink,
      new HttpLink({
        uri: `${constraintsApiUrl}/graphql`,
        ...constraintsOptions,
      }),
    ]),
    cache: new InMemoryCache(),
    ...sharedClientConfig,
  });

  const apolloGatewayClient = new ApolloClient({
    link: from([errorLink, networkInterfaces.gateway]),
    cache: new InMemoryCache(),
    ...sharedClientConfig,
  });

  const accountServicePrivateClient = new ApolloClient({
    link: from([errorLink, networkInterfaces.accountServicePrivateApi]),
    cache: new InMemoryCache(),
    ...sharedClientConfig,
  });

  const planningServiceClient = new ApolloClient({
    link: from([errorLink, networkInterfaces.planningService]),
    cache: new InMemoryCache(),
    ...sharedClientConfig,
  });

  // Use the shared routedClient from React as the default client
  // This ensures Vue and React share the same Apollo cache
  return new VueApollo({
    clients: {
      api: routedClient,
      constraintsApi: apolloConstraintsClient,
      accountServicePrivateApi: accountServicePrivateClient,
      gateway: apolloGatewayClient,
      planningService: planningServiceClient,
    },
    defaultClient: routedClient,
  });
};
